import { type Size } from "./vision.ts";

/**
 * Where the minimap's ground comes from (P1.9-T178; ADR-061's amendment of 2026-10-14): pure whole-number geometry
 * from the player's position and a field to the rectangle of the map's picture the field copies, and the rule for when
 * a cropped picture no longer holds the player's vision. The picture is the map at the field's scale, one canvas pixel
 * to a field pixel, so the copy is a straight rectangle, never scaled. The copy spans the whole field wherever the
 * picture reaches (the author's ruling of 2026-10-14: no dark band outside the vision rectangle); what is drawn on it,
 * the rectangle's edge and the marks, is the painter's.
 */

/**
 * What the picture is: its size in pixels, and the map tile its top left corner shows (nonzero for a map longer than
 * the picture's most pixels at the scale).
 */
export interface GroundPicture {
  readonly width: number;
  readonly height: number;
  readonly cropX: number;
  readonly cropY: number;
}

/**
 * A rectangle of a picture and where it lands in the field: `sx` and `sy` in the picture's pixels, `dx` and `dy` in
 * the field's, the same `width` and `height` in both.
 */
export interface GroundView {
  sx: number;
  sy: number;
  dx: number;
  dy: number;
  width: number;
  height: number;
}

/**
 * A view to write into, so the painter allocates none while the player walks.
 *
 * @returns A zeroed view.
 */
export function newGroundView(): GroundView {
  return { sx: 0, sy: 0, dx: 0, dy: 0, width: 0, height: 0 };
}

/**
 * The player's position along one axis in whole pixels of the field's scale: what the picture is shifted by, so it
 * moves a canvas pixel at a time and the same position always draws the same pixels.
 *
 * @param position The player's position, in tiles.
 * @param scale Logical pixels a tile.
 * @returns A whole number of pixels.
 */
export function minimapOffset(position: number, scale: number): number {
  return Math.round(position * scale);
}

/**
 * Works out which rectangle of a picture a field copies, and where. The player is at the field's middle; the rectangle
 * is the whole field, cut to the picture.
 *
 * @param out Receives the view when there is one.
 * @param field The field's size in logical pixels.
 * @param scale Logical pixels a tile.
 * @param x The player's position across, in tiles.
 * @param y The player's position down, in tiles.
 * @param picture The picture the field copies from.
 * @returns False when no pixel of the picture lies inside the field, `out` then unchanged.
 */
export function groundView(out: GroundView, field: Size, scale: number, x: number, y: number, picture: GroundPicture): boolean {
  const centreX = Math.round(field.width / 2);
  const centreY = Math.round(field.height / 2);
  const offsetX = minimapOffset(x, scale);
  const offsetY = minimapOffset(y, scale);
  const pictureLeft = centreX - offsetX + picture.cropX * scale;
  const pictureTop = centreY - offsetY + picture.cropY * scale;
  const left = Math.max(0, pictureLeft);
  const right = Math.min(field.width, pictureLeft + picture.width);
  const top = Math.max(0, pictureTop);
  const bottom = Math.min(field.height, pictureTop + picture.height);
  if (right <= left || bottom <= top) {
    return false;
  }
  out.sx = left - pictureLeft;
  out.sy = top - pictureTop;
  out.dx = left;
  out.dy = top;
  out.width = right - left;
  out.height = bottom - top;
  return true;
}

/**
 * Whether a cropped picture still holds the whole of the field, so far as the map reaches: the field's extent in tiles
 * about the player, cut to the map, lies inside the tiles the picture shows.
 *
 * @param x The player's position across, in tiles.
 * @param y The player's position down, in tiles.
 * @param crop The map tile the picture's top left corner shows.
 * @param shownWidth The tiles the picture shows across.
 * @param shownHeight The tiles the picture shows down.
 * @param map The map's size in tiles.
 * @param reachWidth The field's width in tiles at its scale (its pixels over the scale).
 * @param reachHeight The field's height in tiles at its scale.
 * @returns True when nothing of the field that the map holds is missing from the picture.
 */
export function minimapCropCovers(x: number, y: number, crop: { readonly x: number; readonly y: number }, shownWidth: number, shownHeight: number, map: Size, reachWidth: number, reachHeight: number): boolean {
  const left = Math.max(0, Math.floor(x - reachWidth / 2));
  const right = Math.min(map.width, Math.ceil(x + reachWidth / 2));
  const top = Math.max(0, Math.floor(y - reachHeight / 2));
  const bottom = Math.min(map.height, Math.ceil(y + reachHeight / 2));
  return left >= crop.x && right <= crop.x + shownWidth && top >= crop.y && bottom <= crop.y + shownHeight;
}
